import React, { useState } from "react";
import { compactPrice } from "@/helpers/compactPrice";
import "./BrokerageHeader.scss";
import AppLink from "@/components/AppLink";

export const monogramOf = (name) =>
  String(name || "")
    .replace(/[^A-Za-z0-9 &]/g, " ")
    .split(" ")
    .filter((word) => word && !/^(llc|inc|the|of|and|&)$/i.test(word))
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");

export const websiteLabel = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
};

const HTTP_URL = /^https?:\/\//i;

const NBSP = "\u00a0";

export const officesLabel = (offices) => {
  const cities = (offices?.cities || []).filter(Boolean);
  if (!cities.length) return "";
  const items = offices.more ? [...cities, "more"] : cities;
  const last = items[items.length - 1];
  const list =
    items.length === 1
      ? last
      : `${items.slice(0, -1).join(", ")} and${NBSP}${last.replace(/ /g, NBSP)}`;
  return `${offices.single ? "Office" : "Offices"} in ${list}`;
};

const PencilIcon = () => (
  <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
    <path d="m10.6 2.6 2.8 2.8-7.9 7.9H2.7v-2.8z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
  </svg>
);

const ExternalIcon = () => (
  <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
    <path d="M6 3h7v7M13 3 6.5 9.5M11 9v4H3V5h4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
    <path
      d="M5.2 2.5 6.6 5 5.4 6.3a7.6 7.6 0 0 0 4.3 4.3L11 9.4l2.5 1.4-.6 2.3c-.2.6-.8 1-1.4.9C6.6 13.4 2.6 9.4 2 4.5c-.1-.6.3-1.2.9-1.4z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
  </svg>
);

export const metaLine = (brokerage) =>
  [officesLabel(brokerage?.offices), brokerage?.networks || ""].filter(Boolean).join(" · ");

const BrokerageHeader = ({ brokerage, name, manageHref, preview = false, editSlots = null }) => {
  const [aboutOpen, setAboutOpen] = useState(false);
  const [failedLogo, setFailedLogo] = useState(null);
  const [failedCover, setFailedCover] = useState(null);
  const title = brokerage?.name || name;
  const logo = brokerage?.logoUrl && brokerage.logoUrl !== failedLogo ? brokerage.logoUrl : null;
  const cover = brokerage?.coverUrl && brokerage.coverUrl !== failedCover ? brokerage.coverUrl : null;
  const about = (brokerage?.about || "").trim();
  const longAbout = about.length > 280;
  const hasDemand = brokerage && brokerage.allTimeDemand > 0;
  const meta = metaLine(brokerage);
  const website = HTTP_URL.test(brokerage?.website || "") ? brokerage.website : null;

  return (
    <section className={`brokerage-header${preview ? " is-preview" : ""}`}>
      <div className={`brokerage-header__cover${cover ? " has-image" : ""}`}>
        {cover ? <img src={cover} alt="" onError={() => setFailedCover(cover)} /> : null}
        {manageHref ? (
          <AppLink className="brokerage-header__edit" href={manageHref}>
            <PencilIcon />
            Edit page
          </AppLink>
        ) : null}
        {editSlots?.cover || null}
      </div>
      <div className="brokerage-header__body">
        <div className={`brokerage-header__logo${logo ? "" : " is-monogram"}`}>
          {logo ? (
            <img src={logo} alt={`${title} logo`} onError={() => setFailedLogo(logo)} />
          ) : (
            <span aria-hidden="true">{monogramOf(title)}</span>
          )}
          {editSlots?.logo || null}
        </div>
        <div className="brokerage-header__title">
          <h1>{title}</h1>
          {meta ? <p className="brokerage-header__meta">{meta}</p> : null}
          {website || brokerage?.phone ? (
            <div className="brokerage-header__actions">
              {website ? (
                <a className="brokerage-header__action" href={website} target="_blank" rel="noopener noreferrer nofollow">
                  <ExternalIcon />
                  {websiteLabel(website)}
                </a>
              ) : null}
              {brokerage?.phone ? (
                <a className="brokerage-header__action" href={`tel:${brokerage.phone.replace(/[^\d+]/g, "")}`}>
                  <PhoneIcon />
                  {brokerage.phone}
                </a>
              ) : null}
            </div>
          ) : null}
        </div>
        {hasDemand ? (
          <dl className="brokerage-header__stats">
            <div>
              <dt>Active demand</dt>
              <dd className={brokerage.activeDemand > 0 ? "" : "is-none"}>
                {brokerage.activeDemand > 0 ? compactPrice(brokerage.activeDemand) : "None now"}
              </dd>
            </div>
            <div>
              <dt>All-time demand</dt>
              <dd>{compactPrice(brokerage.allTimeDemand)}</dd>
            </div>
          </dl>
        ) : null}
      </div>
      {about ? (
        <div className="brokerage-header__about">
          <p className={longAbout && !aboutOpen ? "is-clamped" : ""}>{about}</p>
          {longAbout ? (
            <button type="button" className="brokerage-header__more" onClick={() => setAboutOpen(!aboutOpen)}>
              {aboutOpen ? "Show less" : "Read more"}
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

export default BrokerageHeader;
